import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import styles from './CommandListModal.module.css';
import { CATEGORY_ORDER, SLASH_COMMANDS } from '../helpers/slashCommands';

interface CommandListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CommandListModal: React.FC<CommandListModalProps> = ({ open, onOpenChange }) => {
  const groupedCommands = useMemo(() => {
    return CATEGORY_ORDER.map((category) => ({
      category,
      commands: SLASH_COMMANDS.filter((cmd) => cmd.category === category),
    })).filter(({ commands }) => commands.length > 0);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-label="Slash command list" className={styles.modalContent}>
        <DialogHeader>
          <DialogTitle>Slash Commands</DialogTitle>
          <DialogDescription>
            Quick reference for available slash commands. Start typing <span>/</span> in the chat to
            filter these commands inline.
          </DialogDescription>
        </DialogHeader>

        {groupedCommands.map(({ category, commands }) => (
          <section key={category}>
            <h3 className={styles.groupTitle}>{category}</h3>
            <table className={styles.commandTable}>
              <thead>
                <tr>
                  <th scope="col">Command</th>
                  <th scope="col">Aliases</th>
                  <th scope="col">Description</th>
                  <th scope="col">Usage</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {commands.map((cmd) => (
                  <tr key={cmd.command}>
                    <td className={styles.commandCell}>{cmd.command}</td>
                    <td className={styles.aliasCell}>
                      {cmd.aliases && cmd.aliases.length > 0 ? cmd.aliases.join(', ') : '—'}
                    </td>
                    <td className={styles.descriptionCell}>{cmd.description}</td>
                    <td className={styles.usageCell}>{cmd.usage}</td>
                    <td className={styles.actionCell}>{cmd.action === 'ai' ? 'AI' : 'Built-in'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

        <p className={styles.footerNote}>
          Pro tip: Use <strong>/goal</strong> to align all AI participants before kicking off a
          session.
        </p>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
